import { AxiosService } from './axios.service'
import FileSaver from 'file-saver'

const ORDERS_PATH = 'me/orders'

/** Decodifica o base64 de um documento num Blob salvável (sem depender de fetch/CORS). */
function base64ToBlob(base64, contentType) {
	const binary = atob(base64)
	const bytes = new Uint8Array(binary.length)
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
	return new Blob([bytes], { type: contentType || 'application/octet-stream' })
}

/**
 * Defines the API methods for the client ticket
 */
export class ClientTicketService {
	static baseURL = 'cliente/bilhete'

	/**
	 * Get the order details (tickets, promotion coupon) from the MuviHub "me/orders" API.
	 * "Minhas Compras" pode listar pedidos legado-only sob um GUID sintético (nunca persistido
	 * em orders) — context.orderNumber habilita o fallback do backend ao legado quando o id local
	 * não bate. O fallback ao legado não tem status/data/meio de pagamento próprios (o endpoint de
	 * detalhe do legado não devolve isso) — o backend espera esses três de volta como contexto do
	 * item da lista (senão assume status "Em processamento" por padrão, mesmo pra um pedido já
	 * concluído).
	 * @param {string} orderId The MuviHub order id (GUID)
	 * @param {object} [context] Contexto já conhecido pela listagem (orderNumber, status,
	 * dateCreated, paymentType) — necessário só pro fallback legado-only.
	 */
	static getOrder(orderId, context) {
		const params = context && context.orderNumber
			? {
				orderNumber: context.orderNumber,
				status: context.status || undefined,
				dateCreated: context.dateCreated || undefined,
				paymentType: context.paymentType || undefined,
			}
			: {}
		return AxiosService.getMuviHub(`${ORDERS_PATH}/${orderId}`, { params }).then(
			({ data }) => data.data
		)
	}

	/**
	 * Get the PIX charge of an order awaiting payment - QR Code (base64), "copia e cola" (EMV) and
	 * validity - already in the shape the QR Code modal (PixInfo) expects. The Hub does not store
	 * the EMV: it reads it from the legacy API on each call. When the legacy API is down, slow or
	 * has no record of the charge, `pixEmv` is '' and the modal shows only the QR Code.
	 * @param {string} orderId The MuviHub order id (GUID)
	 * @returns {Promise<{pixImageQrCode: string, pixValidity: string, pixEmv: string}>}
	 */
	static getPix(orderId) {
		return AxiosService.getMuviHub(`${ORDERS_PATH}/${orderId}/pix`).then(({ data }) => ({
			pixImageQrCode: data.data.qrCodeBase64,
			pixValidity: data.data.validity,
			pixEmv: data.data.emv || '',
		}))
	}

	/**
	 * Get the digital ticket documents (BP-e) for an order. Each ticket is one
	 * PDF; the caller saves them one by one. Rejects (with the API's own message,
	 * eg. "Pedido sem documentos disponíveis.") when there's nothing available.
	 *
	 * The MuviHub returns JSON with every BP-e already resolved server-side (the
	 * hosted ones are downloaded there) and embedded as base64 — the browser can't
	 * fetch them straight from the carrier's S3/host (CORS). We decode each into a
	 * ready-to-save Blob.
	 *
	 * @param {string} orderId The MuviHub order id (GUID)
	 * @returns {Promise<{documents: {fileName: string, blob: Blob}[], missing: string[]}>}
	 */
	static getOrderDocuments(orderId) {
		return AxiosService.getMuviHub(`${ORDERS_PATH}/${orderId}/documents`).then(
			({ data }) => {
				const payload = (data && data.data) || {}
				return {
					documents: (payload.documents || []).map((doc) => ({
						fileName: doc.fileName,
						blob: base64ToBlob(doc.contentBase64, doc.contentType),
					})),
					missing: payload.missing || [],
				}
			}
		)
	}

	/**
	 * Return a ticket (legacy backend)
	 */
	static returnTicket({ ticketId, cancelAll, isGoing }) {
		return AxiosService.post(`${this.baseURL}/devolver`, {
			ticketId,
			cancelAll,
			isGoing,
		}).then(({ data }) => data)
	}

	/**
	 * Cancel one or more tickets of an order on the MuviHub API
	 * @param {string} orderId The MuviHub order id (GUID)
	 * @param {string[]} ticketIds
	 */
	static cancelOrderTickets(orderId, ticketIds) {
		return AxiosService.postMuviHub(
			`${ORDERS_PATH}/${orderId}/tickets/cancel`,
			{ ticketIds }
		).then(({ data }) => data.data)
	}

	/**
	 * Resend trip data to user e-mail
	 * @param {number} orderNumber
	 */
	static resendTripData(orderNumber) {
		return AxiosService.post(`${this.baseURL}/reenviar-por-email`, {
			orderNumber,
		}).then(({ data }) => data)
	}

	/**
	 * download BPE files
	 */
	static async download(orderNumber, trackNumber, userId, type) {
		let params = {
			orderNumber: orderNumber,
			trackNumber: trackNumber,
			buyerId: userId,
			type: type,
		}
		return AxiosService.get(`${this.baseURL}/download`, {
			params: params,
			responseType: 'blob',
		})
	}

	static async generateBpeFiles(orderNumber, userId) {
		let params = {
			orderNumber: orderNumber,
			buyerId: userId,
		}
		AxiosService.post(`${this.baseURL}/gerar-arquivos`, params).then(
			({ data }) => data
		)
	}
}
